/**
 * DyoPdpFeatures Web Component
 * ============================================================
 * Finalidade: Altera dinamicamente a exibição dos acordeons de features na PDP,
 * com base no resultado de um modelo dinâmico integrado com Statsig.
 *
 * Estrutura:
 * - Usa StatsigExperimentManager para verificar gates e acessar parâmetros da layer.
 * - Usa ModelManager para buscar o modelo via API /pull/.
 * - Salva o modelo em localStorage para persistência.
 * - Aplica alterações de layout com base na resposta do modelo.
 *
 * Observabilidade:
 * - Usa console.log para pontos de inspeção no DOM.
 * - Envia falhas para Rollbar com contexto (stable_id, gate, layer, etc).
 *
 * Particularidades:
 * - Lógica específica para manipular acordeons e esconde elementos padrões da PDP.
 */
export {};

import {
  ModelManager,
  getSafeStorage,
  getStatsigClient,
  getCartItemsIdsString,
  getOrCreateStatsigManager,
  buildModelRequestPayload,
  shouldSendPayload,
  PayloadField,
  Logger,
  syncDebugFlag,
} from './statsig-manager.js';

// Definição de tipos para a resposta do modelo
interface modelResponse {
  product_description?: string;
  [key: string]: unknown;
}

const OLD_MODEL_KEY = "dyo_test_pdp_features";
const MODEL_KEY = "statsig:test:dyo:pdpfeatures";
const EXPERIMENT = {
  title: "DyoPdpFeatures",
  name: "dyo-pdp-features-v0",
  gate: "dyo-pdp-features",
  layer: "dyo-pdp-features",
};

const DEBUG = false; // Altere para false para desativar logs deste teste

if (!customElements.get("dyo-pdp-features"))
  customElements.define( 
    "dyo-pdp-features",
    class DyoPDPFeatures extends HTMLElement {
      customerId?: string;
      cartItemsIds?: string;
      statsigGate: string;
      statsigLayer: string;
      statsigStableId: string | null;
      allocatedExperiment: string | null;
      modelUrl: string | null;
      REQUEST_TIMEOUT_MS: number;
      modelManager: ModelManager;
      objExperimentInfos: {
        stable_id: string;
        allocated_experiment: string;
        gate: string;
        layer: string;
        cart: string;
        customer_id: string;
        category: string;
        component: string;
      };

      constructor() {
        super();
        this.customerId = this.getAttribute("data-customer-id") || null;
        this.statsigGate =  this.getAttribute("data-statsig-gate") || EXPERIMENT.gate;
        this.statsigLayer = this.getAttribute("data-statsig-layer") || EXPERIMENT.layer;
        this.statsigStableId = null;
        this.allocatedExperiment = EXPERIMENT.name;
        this.modelUrl = null;
        this.REQUEST_TIMEOUT_MS = 10000;
        this.modelManager = new ModelManager();
        this.objExperimentInfos = {
          stable_id: this.statsigStableId,
          gate: this.statsigGate,
          layer: this.statsigLayer,
          cart: getCartItemsIdsString(),
          customer_id: this.customerId,
          allocated_experiment: this.allocatedExperiment,
          category: "Statsig",
          component: `${EXPERIMENT.name}`,
        }
      }

      async connectedCallback(): Promise<void> {
        try {
          syncDebugFlag(EXPERIMENT.name, DEBUG);
          const migrated = this.migrateLegacyModelIfNeeded();
          await getOrCreateStatsigManager();
          this.runTest(migrated);
        } catch (error) {
          Logger.error(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] StatsigManager not ready`, error, this.objExperimentInfos);
        }
      }

      // Função principal que executa o fluxo do experimento
      async runTest(migrated?: boolean): Promise<void> {
        
        Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Executando runTest(): `, EXPERIMENT);
        try {
          const stableContext = await getStatsigClient()?.getAsyncContext();
          this.statsigStableId = stableContext?.stableID ?? null;
          
          const persistedModelData = this.loadModelFromStorage();
          if (persistedModelData) {
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Reusing persisted group data: `, persistedModelData);
            this.applyLayoutChanges(persistedModelData.product_description);
          }

          const experimentManager = await getOrCreateStatsigManager();
          const gateOpen: boolean = await experimentManager.checkGate(this.statsigGate);
          const shouldPersist = gateOpen || migrated;

          if (!shouldPersist) {
            Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Gate closed and no migration. Exiting without persisting.`, this.objExperimentInfos);
            return;
          }

          const triggers = ["first_load", "stable_id"];
          const shouldSend = shouldSendPayload(triggers, MODEL_KEY, "every");
          if (!shouldSend) {
            Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Not available to send payload. Aborting...`, triggers, this.objExperimentInfos);
            return;
          }

          const layer = await experimentManager.getLayerParams(this.statsigLayer);
          if (!layer) {
            Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Layer not found. Exiting.`, this.objExperimentInfos);
            return;
          }

          this.modelUrl = layer.get("modelUrl");

          if (!this.modelUrl) {
            Logger.error(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] No model URL found.`, this.objExperimentInfos);
            return;
          }

          const includeFields: PayloadField[] = [
            "customer_id",
            "stable_id",
            "allocated_experiment",
            "cart",
            "utms",
            "products",
            "collections"
          ];
      
          const payload = buildModelRequestPayload(
            {
              customer_id: this.customerId ?? null,
              stable_id: this.statsigStableId ?? undefined,
              allocated_experiment: this.allocatedExperiment,
            }, includeFields
          );
          
          Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Payload to request: `, payload);

          const modelData = await this.modelManager.getModel(
            this.modelUrl,
            payload,
            { timeout: this.REQUEST_TIMEOUT_MS },
            MODEL_KEY,
            includeFields,
          ) as modelResponse;

          Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Applying layout using new data received from model group at first time:`, modelData);
          this.applyLayoutChanges(modelData?.product_description);
        } catch (error) {
          Logger.error(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Error in runTest()`, error, this.objExperimentInfos);
        }
      }

      // Aplica as alterações no layout da página de acordo com a descrição do produto retornada pelo modelo
      private applyLayoutChanges(productDescription: string): void {
        // Aplica somente na página de produto, exceto kits
        if (window.location.href.includes("/products/") && !window.location.href.includes("kit")) {
          // Caso padrão, não altera nada
          if (productDescription === "default") {
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Default product description`);
            return;
          }

          // Seleciona todos os elementos de acordeon do produto
          const accordions = Array.from(document.getElementsByClassName("product__accordion") ?? []);
          if (accordions.length < 4) return;

          // Exibe todos os acordeons
          accordions.forEach((accordion) => {
            if (accordion instanceof HTMLElement) {
              accordion.style.display = "block";
            }
          });

          // Esconde elementos específicos para preparar o layout customizado
          const title = document.querySelector(".product-features h3");
          const glide = document.querySelector(".product-features glide-component");
          const description = document.querySelector(".product__description");

          if (title instanceof HTMLElement) title.style.display = "none";
          if (glide instanceof HTMLElement) glide.style.display = "none";
          if (description instanceof HTMLElement) description.style.display = "none";

          // Controla o estado dos acordeons conforme a descrição do produto
          if (productDescription === "accordion_opened") {
            document.getElementsByClassName("product__accordion")[0].querySelector("details")?.setAttribute("open", "true");
            document.getElementsByClassName("product__accordion")[1].querySelector("details")?.setAttribute("open", "true");
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Accordion opened`);
          } else if (productDescription === "accordion_closed") {
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Accordion closed`);
          }
        }
      }

      // Função para migrar dados do modelo legado para o novo formato
      private migrateLegacyModelIfNeeded(): boolean {
        try {
          const storage = getSafeStorage();
          const legacyData = storage.getItem(OLD_MODEL_KEY);
          const parsedLegacy = legacyData ? JSON.parse(legacyData) : null;

          if (parsedLegacy && !storage.getItem(MODEL_KEY)) {
            storage.setItem(MODEL_KEY, JSON.stringify(parsedLegacy));

            const stableId = storage.getItem("STATSIG_LOCAL_STORAGE_STABLE_ID");
            if (stableId) {
              storage.setItem(`${MODEL_KEY}:stableid`, stableId);
            }

            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Legacy model data migrated from ${OLD_MODEL_KEY} to ${MODEL_KEY}`);
            return true;
          }

          if (legacyData && storage.getItem(MODEL_KEY)) {
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Possible rollback detected: ${OLD_MODEL_KEY} still exists`);
          }
        } catch (error) {
          Logger.error(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Failed to migrate legacy model data`, error, this.objExperimentInfos);
        }
        return false;
      }

      // Carrega os dados do modelo persistidos no localStorage, se existirem
      private loadModelFromStorage(): modelResponse | null {
        const storage = getSafeStorage();
        let parsed = null;
        try {
          const raw = storage.getItem(MODEL_KEY);
          parsed = raw ? JSON.parse(raw) : null;
        } catch {
          parsed = null;
        }

        if (!parsed || typeof parsed !== "object") {
          return null;
        }

        return parsed;
      }
    }
  );
